import { useEffect, useState } from "react";

const SIDEBAR_PERSIST_KEY = "remi-layout-sidebar-collapsed";
const SIDEBAR_WIDTH_COLLAPSED = 64;
const SIDEBAR_WIDTH_EXPANDED = 256;
const EDGE_PADDING = 24;
const BANNER_HEIGHT = 44; // h-11 escalation banner

export interface ViewportBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  maxWidth: number;
  maxHeight: number;
}

function readSidebarWidth(): number {
  if (typeof window === "undefined") {
    return SIDEBAR_WIDTH_EXPANDED;
  }
  const raw = window.localStorage.getItem(SIDEBAR_PERSIST_KEY);
  return raw === "true" ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
}

function computeBounds(): ViewportBounds {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, right: 0, bottom: 0, maxWidth: 0, maxHeight: 0 };
  }
  const sidebarWidth = readSidebarWidth();
  const left = sidebarWidth + EDGE_PADDING;
  const top = BANNER_HEIGHT + EDGE_PADDING;
  const right = window.innerWidth - EDGE_PADDING;
  const bottom = window.innerHeight - EDGE_PADDING;
  return {
    left,
    top,
    right,
    bottom,
    maxWidth: Math.max(0, right - left),
    maxHeight: Math.max(0, bottom - top),
  };
}

export function useViewportBounds(): ViewportBounds {
  const [bounds, setBounds] = useState<ViewportBounds>(computeBounds);

  useEffect(() => {
    // Throttle to rAF so window-resize storms don't re-render every popout subtree per tick.
    let frame = 0;
    const handler = () => {
      if (frame !== 0) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        setBounds(computeBounds());
      });
    };
    window.addEventListener("resize", handler);
    window.addEventListener("storage", handler);
    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener("resize", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return bounds;
}
